import React, { useEffect, useState } from 'react';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Button from '../ui/Button';
import Icon from '../AppIcon';
import repositoryContainer from '../../services/RepositoryContainer';
import { formatPaymentAmount } from './PaymentSummary';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'bankTransfer', label: 'Bank Transfer' },
  { value: 'mobileMoney', label: 'Mobile Money' },
  { value: 'pos', label: 'POS' },
  { value: 'other', label: 'Other' },
];

const createIdempotencyKey = () => (
  globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`
);

const ManualPaymentModal = ({
  isOpen,
  onClose,
  sourceType,
  sourceId,
  balanceDue,
  canPayAndComplete = false,
  onRecorded,
}) => {
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [note, setNote] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setAmount((Number(balanceDue) || 0).toFixed(2));
    setPaymentMethod('');
    setNote('');
    setError('');
    setIdempotencyKey(createIdempotencyKey());
  }, [isOpen, sourceId]);

  const handleSubmit = async (event, markAsCompleted = false) => {
    event?.preventDefault();
    const numericAmount = Number(amount);
    const currentBalance = Number(balanceDue) || 0;

    if (!paymentMethod) {
      setError('Select how this payment was received.');
      return;
    }
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setError('Enter a payment amount greater than zero.');
      return;
    }
    if (numericAmount > currentBalance) {
      setError(`The amount cannot exceed the ${formatPaymentAmount(currentBalance)} balance.`);
      return;
    }

    const isFullBalance = Math.abs(numericAmount - currentBalance) < 0.005;
    if (markAsCompleted && !isFullBalance) {
      setError(`Pay and complete requires the full remaining balance of ${formatPaymentAmount(currentBalance)}.`);
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      const payload = {
        sourceType,
        sourceId,
        paymentMethod,
        note: note.trim() || null,
        idempotencyKey,
        markAsCompleted,
      };
      if (!isFullBalance) payload.amount = numericAmount;

      const raw = await repositoryContainer.payment.recordManual(payload);
      const result = raw?.response ?? raw;
      await onRecorded?.(result);
      onClose();
    } catch (submissionError) {
      setError(submissionError?.data?.message || 'The payment could not be recorded. You can safely retry.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => !isSubmitting && onClose()} title="Record payment" maxWidth="max-w-lg">
      <form onSubmit={(event) => handleSubmit(event, false)} className="space-y-5">
        <div className="flex items-start gap-3 rounded-xl bg-primary/5 p-4">
          <Icon name="ShieldCheck" size={20} className="mt-0.5 flex-shrink-0 text-primary" />
          <div>
            <p className="text-sm font-medium text-foreground font-caption">Remaining balance: {formatPaymentAmount(balanceDue)}</p>
            <p className="mt-1 text-xs text-muted-foreground font-caption">
              Use this only for money received outside the platform.
            </p>
          </div>
        </div>

        <Input
          label="Amount received"
          type="number"
          min="0.01"
          max={Number(balanceDue) || undefined}
          step="0.01"
          value={amount}
          onChange={(event) => { setAmount(event.target.value); setError(''); }}
          required
          disabled={isSubmitting}
        />
        <Select
          label="Payment method"
          options={PAYMENT_METHODS}
          value={paymentMethod}
          onChange={(value) => { setPaymentMethod(value); setError(''); }}
          placeholder="Select a payment method"
          required
          disabled={isSubmitting}
        />
        <div>
          <label htmlFor="manual-payment-note" className="mb-2 block text-sm font-medium text-foreground font-caption">
            Note <span className="text-muted-foreground">(optional)</span>
          </label>
          <textarea
            id="manual-payment-note"
            rows={3}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            disabled={isSubmitting}
            placeholder="For example: Received at the front desk"
            className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          />
        </div>

        {error && <p className="text-sm text-error font-caption">{error}</p>}

        {canPayAndComplete && (
          <div className="rounded-xl bg-success/10 px-4 py-3 text-xs leading-relaxed text-foreground font-caption">
            Pay and complete records the full remaining balance and completes this {sourceType === 'bookingRequest' ? 'request' : sourceType} in one step.
          </div>
        )}

        <div className="flex flex-col gap-3 border-t border-border pt-5 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button type="submit" loading={isSubmitting} iconName="Receipt">Record payment</Button>
          {canPayAndComplete && (
            <Button
              type="button"
              variant="success"
              loading={isSubmitting}
              iconName="CircleCheck"
              onClick={(event) => handleSubmit(event, true)}
            >
              Pay and complete
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default ManualPaymentModal;
